import { getActionRecommendations } from './sharingTipIdMaps/getActionRecommendations';
import { getCustomActions } from './sharingTipIdMaps/getCustomActions';
import { IdType, SharingData } from '../schema/SharingData';
import { SharingTipId } from '../types/sharingTipId';
import { SharingTipRecipientInfo } from '../types/SharingTipRecipientInfo';
import { getIssueDetector } from '../utils/sharingTipIdMaps/getIssueDetector';
import { isSharingTipEnabled } from '../utils/sharingTipIdMaps/isSharingTipEnabled';
import { getInfoBarMessageRank } from '../utils/sharingTipIdMaps/getInfoBarMessageRank';
import { sharingTipIdToInfoBarString } from '../utils/sharingTipIdMaps/sharingTipIdToInfoBarString';
import InfoBarMessageViewStateCreator from 'owa-info-bar/lib/schema/InfoBarMessageViewStateCreator';
import { format } from 'owa-strings';
import { isStringNullOrWhiteSpace } from 'owa-strings';
import { trace } from 'owa-trace';
import {
    InfoBarCustomAction,
    InfoBarMessageRank,
    InfoBarMessageSource,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';

// TODO VSO 32530: Deciding if the infobar has custom actions, and what they are, should be done here. Passing in the downsell action is merely a bandaid
// to maintain existing functionality while I (kcurlett) rewrite sharing tips/infobar actions to be satchelV3 actions.
export function getInfoBarCreator(
    sharingData: SharingData[],
    sharingTipId: SharingTipId,
    recipientInfo: SharingTipRecipientInfo[],
    convertCloudyToClassicAction?: (id: IdType) => InfoBarCustomAction
): InfoBarMessageViewStateCreator | null {
    if (!isSharingTipEnabled(sharingTipId)) {
        return null;
    }
    const sharingTipIssueDetector: (
        sharingData: SharingData,
        recipientInfo: SharingTipRecipientInfo[]
    ) => boolean = getIssueDetector(sharingTipId);
    const sharingDataWithIssue: SharingData[] = sharingData.filter(data =>
        sharingTipIssueDetector(data, recipientInfo)
    );

    if (sharingDataWithIssue.length === 0) {
        return null;
    }

    const fileNames = sharingDataWithIssue
        .filter(x => !isStringNullOrWhiteSpace(x.name))
        .map(x => x.name)
        .join(', ');

    const infoBarStringTemplate: string =
        sharingDataWithIssue.length > 1
            ? sharingTipIdToInfoBarString(sharingTipId, recipientInfo).multipleFiles
            : sharingTipIdToInfoBarString(sharingTipId, recipientInfo).singleFile;

    if (
        (!fileNames || fileNames.length === 0) &&
        infoBarStringTemplate &&
        infoBarStringTemplate.length !== 0
    ) {
        trace.error('file names should not be null or empty if the infobarStringTemplate is not');
    }

    const customActions: InfoBarCustomAction[] = getCustomActions(
        sharingDataWithIssue,
        sharingTipId,
        convertCloudyToClassicAction
    );

    const messageRank: InfoBarMessageRank = getInfoBarMessageRank(sharingTipId);

    if (customActions && customActions.length > 0) {
        const infoBarString = format(infoBarStringTemplate, fileNames);
        const infoBarMessageViewState: InfoBarMessageViewState = {
            key: sharingTipId,
            source: InfoBarMessageSource.Compose,
            rank: messageRank,
            messageParts: [infoBarString],
        };

        customActions.map(action => {
            infoBarMessageViewState.messageParts.push(action);
        });

        // viewState in this case will be a ComposeViewState. We can't use that type though since it will create a circular dependency.
        return (viewState: any /*ComposeViewState */) => infoBarMessageViewState;
    }

    // If we don't have a custom action that can fix the problem then we append a string recommending a fix to the user, if possible.
    let actionString = '';
    const actionRecommendation = getActionRecommendations(sharingTipId);
    if (actionRecommendation !== null) {
        actionString =
            sharingDataWithIssue.length > 1
                ? actionRecommendation.multipleFiles
                : actionRecommendation.singleFile;
    }
    const infoBarString = format(infoBarStringTemplate, fileNames, actionString);
    const infoBarMessageViewState: InfoBarMessageViewState = {
        key: sharingTipId,
        source: InfoBarMessageSource.Compose,
        rank: messageRank,
        messageParts: [infoBarString],
    };

    // viewState in this case will be a ComposeViewState. We can't use that type though since it will create a circular dependency.
    return (viewState: any /*ComposeViewState */) => infoBarMessageViewState;
}
